/**
 * Document Template Edit Modal (Organism)
 * Uses React Hook Form + Zod; no direct API calls.
 * - 閉じる / (X): show confirm dialog when form has unsaved changes; close without dialog when not dirty.
 * - Browser reload/close: beforeunload when modal open and dirty.
 * - Click outside modal: does not close (maskClosable=false).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import { useMainDocumentTemplateDetail } from '@/hooks/main/useMainDocumentTemplate';
import {
  documentTemplateFormSchema,
  type DocumentTemplateFormData,
} from '@/schemas/documentTemplateSchema';
import type { DocumentTemplate, DocumentType, SuffixType } from '@/types/documentTemplate.types';
import {
  AppTextArea,
  AppRadioGroup,
  AppRadio,
  AppCheckbox,
  AppTextField,
  AppButton,
  AppModal,
  AppForm,
  AppFormLabel,
  AppSpin,
  AppTypography,
} from '@/components/atoms';

/** Convert half-width to full-width (全角変換) for title/prefix/suffix */
function toFullWidth(str: string): string {
  if (!str) return str;
  return str
    .replace(/[A-Za-z0-9]/g, char => String.fromCharCode(char.charCodeAt(0) + 0xfee0))
    .replace(/ /g, '\u3000')
    .replace(/[\uFF61-\uFF9F]/g, char => {
      const halfKatakana = 'ｦｧｨｩｪｫｬｭｮｯｰｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝﾞﾟ';
      const fullKatakana =
        'ヲァィゥェォャュョッーアイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワン゛゜';
      const index = halfKatakana.indexOf(char);
      return index >= 0 ? fullKatakana[index] : char;
    });
}

/** Use Japanese message for known fields when API returns validation errors */
const FIELD_ERROR_MESSAGE_JA: Partial<Record<keyof DocumentTemplateFormData, string>> = {
  document_type_id: '種類は必須です。',
  title: '表題は必須です。',
  suffix_type_id: '敬称は必須です。',
};

export interface DocumentTemplateEditModalProps {
  templateId: number | null;
  documentTypes: DocumentType[];
  suffixTypes: SuffixType[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: DocumentTemplateFormData) => Promise<void>;
  isNew?: boolean;
}

// HIT-4745: default honorific (敬称) for a new 御礼状 / 発行依頼 template is 先生
// (suffix_types id 2), not 様. The user can still change it before saving.
const SUFFIX_TYPE_SENSEI = 2;

const INITIAL_EMPTY: DocumentTemplateFormData = {
  document_type_id: undefined,
  is_default: false,
  title: '',
  content_prefix: '',
  content: '',
  content_suffix: '',
  suffix_type_id: SUFFIX_TYPE_SENSEI,
};

const DocumentTemplateEditModal: React.FC<DocumentTemplateEditModalProps> = ({
  templateId,
  documentTypes,
  suffixTypes,
  isOpen,
  onClose,
  onSave,
  isNew = false,
}) => {
  const { colors } = useTheme();
  const toast = useAppToast();
  const [initialFormData, setInitialFormData] = useState<DocumentTemplateFormData>(INITIAL_EMPTY);

  const isEditMode = isOpen && !isNew && !!templateId;
  const {
    data: detailResponse,
    isLoading: detailLoading,
    isFetching: detailFetching,
    isError: isDetailError,
    error: detailError,
  } = useMainDocumentTemplateDetail(isEditMode ? templateId ?? 0 : 0);

  const template: DocumentTemplate | null = detailResponse?.data ?? null;
  const showDetailLoading = isEditMode && (detailLoading || detailFetching) && !isDetailError;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useTrimmedForm<DocumentTemplateFormData>({
    resolver: zodResolver(documentTemplateFormSchema),
    mode: 'onBlur',
    defaultValues: INITIAL_EMPTY,
  });

  const contentValue = watch('content');
  const formValues = watch();

  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(formValues, initialFormData);

  useEffect(() => {
    if (!isOpen) {
      reset(INITIAL_EMPTY);
      setInitialFormData(INITIAL_EMPTY);
      return;
    }
    if (template && !isNew) {
      const initial = {
        document_type_id: template.document_type_id,
        is_default: template.is_default,
        title: template.title || '',
        content_prefix: template.content_prefix || '',
        content: template.content || '',
        content_suffix: template.content_suffix || '',
        suffix_type_id: template.suffix_type_id,
      };
      reset(initial);
      setInitialFormData(initial);
    } else {
      reset(INITIAL_EMPTY);
      setInitialFormData(INITIAL_EMPTY);
    }
  }, [isOpen, template, isNew, suffixTypes, reset]);

  useEffect(() => {
    if (!isEditMode) return;
    if (!isDetailError || !detailError) return;
    const msg = isNotFoundError(detailError)
      ? 'データが見つかりません。'
      : getApiErrorMessage(detailError, 'テンプレートの取得に失敗しました。');
    toast.error(msg);
    onClose();
  }, [detailError, isDetailError, isEditMode, onClose, toast]);

  const handleRequestClose = useCallback(() => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  }, [confirmIfChanged, clearFormChange, onClose]);

  const onSubmit = async (data: DocumentTemplateFormData) => {
    try {
      await onSave(data);
      clearFormChange();
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { errors?: Record<string, string[]> } } };
        if (axiosError.response?.data?.errors) {
          Object.entries(axiosError.response.data.errors).forEach(([key, value]) => {
            const field = key as keyof DocumentTemplateFormData;
            const rawMessage = Array.isArray(value) ? value[0] : String(value);
            const message = FIELD_ERROR_MESSAGE_JA[field] ?? rawMessage;
            setError(field, { type: 'manual', message });
          });
        }
      }
    }
  };

  const insertPatientName = () => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement | null;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = contentValue ?? '';
      const newText = text.substring(0, start) + '@受診者名挿入@' + text.substring(end);
      setValue('content', newText);
    }
  };

  if (!isOpen) return null;
  if (!isNew && !templateId) return null;

  // footer buttons: 閉じる = close (with confirm if dirty);
  const modalFooter = [
    <AppButton
      key="save"
      type="primary"
      onClick={handleSubmit(onSubmit)}
      disabled={isSubmitting}
      loading={isSubmitting}
    >
      {isSubmitting ? '保存中...' : '保存'}
    </AppButton>,
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleRequestClose}
      title={isNew ? '文章テンプレート登録' : '文章テンプレート編集'}
      maxWidth="max-w-3xl"
      footer={modalFooter}
      styles={{
        header: { backgroundColor: colors.sectionHeader, color: colors.sectionHeaderText },
      }}
    >
      <div className="relative">
        {showDetailLoading && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70 backdrop-blur-sm"
            aria-busy="true"
            aria-label="読み込み中"
          >
            <AppSpin size="large" />
          </div>
        )}
        {isDetailError && isEditMode && !showDetailLoading && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
            <AppTypography.P className="mb-3">
              {detailError?.message || 'テンプレートの取得に失敗しました。'}
            </AppTypography.P>
            <div className="flex justify-end">
              <AppButton type="primary" onClick={handleRequestClose}>
                閉じる
              </AppButton>
            </div>
          </div>
        )}
      <form onSubmit={handleSubmit(onSubmit)} className="p-4">
        <div className="space-y-4">
          <AppForm.Item
            label={
              <>
                <AppFormLabel label="種類" required />
              </>
            }
            validateStatus={errors.document_type_id ? 'error' : undefined}
            help={errors.document_type_id?.message}
            layout="vertical"
            className="mb-0"
          >
            <Controller
              name="document_type_id"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  value={field.value}
                  onChange={e =>
                    field.onChange(
                      Number((e as unknown as { target: { value: string | number } }).target.value)
                    )
                  }
                >
                  {documentTypes.map(type => (
                    <AppRadio key={type.id} value={type.id}>
                      {type.name}
                    </AppRadio>
                  ))}
                </AppRadioGroup>
              )}
            />
          </AppForm.Item>

          <div>
            <Controller
              name="is_default"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={field.value}
                  onChange={e =>
                    field.onChange(
                      (e as unknown as { target: { checked: boolean } }).target.checked
                    )
                  }
                >
                  デフォルトテンプレートにする
                </AppCheckbox>
              )}
            />
          </div>

          <AppForm.Item
            label={
              <>
                <AppFormLabel label="表題" required />
              </>
            }
            validateStatus={errors.title ? 'error' : undefined}
            help={errors.title?.message}
            layout="vertical"
            className="mb-0"
          >
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={() => {
                    field.onChange(toFullWidth(field.value ?? ''));
                    field.onBlur();
                  }}
                  className="w-full px-3 py-2"
                  maxLength={255}
                  status={errors.title ? 'error' : undefined}
                  placeholder="入力してください"
                />
              )}
            />
          </AppForm.Item>

          <div>
            <AppFormLabel label="始め" />
            <Controller
              name="content_prefix"
              control={control}
              render={({ field }) => (
                <AppTextField
                  value={field.value ?? ''}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={() => {
                    field.onChange(toFullWidth(field.value ?? ''));
                    field.onBlur();
                  }}
                  className="w-full px-3 py-2"
                  maxLength={255}
                />
              )}
            />
          </div>

          <div>
            <AppFormLabel label="結び" />
            <Controller
              name="content_suffix"
              control={control}
              render={({ field }) => (
                <AppTextField
                  value={field.value ?? ''}
                  onChange={e => field.onChange(e.target.value)}
                  onBlur={() => {
                    field.onChange(toFullWidth(field.value ?? ''));
                    field.onBlur();
                  }}
                  className="w-full px-3 py-2"
                  maxLength={255}
                />
              )}
            />
          </div>

          <AppForm.Item
            label={
              <>
                <AppFormLabel label="敬称" required />
              </>
            }
            validateStatus={errors.suffix_type_id ? 'error' : undefined}
            help={errors.suffix_type_id?.message}
            layout="vertical"
            className="mb-0"
          >
            <Controller
              name="suffix_type_id"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  value={field.value}
                  onChange={e =>
                    field.onChange(
                      Number((e as unknown as { target: { value: string | number } }).target.value)
                    )
                  }
                >
                  {suffixTypes.map(type => (
                    <AppRadio key={type.id} value={type.id}>
                      {type.name}
                    </AppRadio>
                  ))}
                </AppRadioGroup>
              )}
            />
          </AppForm.Item>

          <div>
            <AppFormLabel label="本文" />
            <div className="mb-2">
              <AppButton
                type="default"
                htmlType="button"
                onClick={insertPatientName}
                className="px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
              >
                受診者名挿入
              </AppButton>
            </div>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <AppTextArea
                  id="template-content"
                  value={field.value ?? ''}
                  onChange={e => field.onChange(e.target.value)}
                  className="w-full !h-64 px-3 py-2"
                  maxLength={1000}
                />
              )}
            />
          </div>
        </div>
      </form>
      </div>
    </AppModal>
  );
};

export default DocumentTemplateEditModal;
